/**
 * Route incoming MIDI note paths → live audition on the active instrument slot.
 *
 * Mirrors `MusicalKeyboard` voice modes:
 * - mono (synth / wavetable): held-note stack + last-note retrigger on release
 * - poly (sf2 / drums / sampler): each key gets its own noteOff
 */

import type { InstrumentSlot } from "../../../../src/lib/audio";
import { getLabActions } from "../labActions";
import {
  controlRegistry,
  normalizeControlPath,
  type ControlMessage,
} from "./controlRegistry";

export type MidiAuditionVoiceMode = "mono" | "poly";

let getAuditionSlot: (() => InstrumentSlot) | null = null;
let getVoiceMode: ((slot: InstrumentSlot) => MidiAuditionVoiceMode) | null = null;
let unsub: (() => void) | null = null;

const held = new Set<number>();
const heldOrder: number[] = [];
/** Last noteOn velocity per note — used when re-triggering after mono release. */
const heldVelocity = new Map<number, number>();

export function setMidiAuditionSlotGetter(getter: () => InstrumentSlot): void {
  getAuditionSlot = getter;
}

export function setMidiAuditionVoiceModeGetter(
  getter: (slot: InstrumentSlot) => MidiAuditionVoiceMode,
): void {
  getVoiceMode = getter;
}

export function startMidiNoteAudition(): void {
  stopMidiNoteAudition();
  unsub = controlRegistry.subscribe(message => {
    if (message.source !== "midi-in") {
      return;
    }
    handleMidiNoteMessage(message);
  });
}

export function stopMidiNoteAudition(): void {
  unsub?.();
  unsub = null;
  releaseAllHeld();
}

function handleMidiNoteMessage(message: ControlMessage): void {
  const path = normalizeControlPath(message.path);
  const parts = path.split("/").filter(Boolean);
  if (parts.length < 2) {
    return;
  }
  const cmd = parts[parts.length - 2]?.toLowerCase();
  const noteTok = parts[parts.length - 1] ?? "";
  if ((cmd !== "noteon" && cmd !== "noteoff") || !/^\d+$/.test(noteTok)) {
    return;
  }
  const note = Number(noteTok);
  if (note < 0 || note > 127) {
    return;
  }

  if (cmd === "noteoff") {
    noteOff(note);
    return;
  }

  const rawVel = Number(message.args[0] ?? 100) || 0;
  if (rawVel <= 0) {
    // Note-on with velocity 0 ≡ note-off
    noteOff(note);
    return;
  }
  const velocity = Math.min(1, Math.max(0.02, rawVel / 127));
  noteOn(note, velocity);
}

function noteOn(note: number, velocity: number): void {
  if (held.has(note)) {
    return;
  }
  held.add(note);
  heldOrder.push(note);
  heldVelocity.set(note, velocity);

  const slot = auditionSlot();
  getLabActions()?.instrumentNoteOn?.(slot, note, velocity);
}

function noteOff(note: number): void {
  if (!held.has(note)) {
    return;
  }
  held.delete(note);
  heldVelocity.delete(note);
  const index = heldOrder.lastIndexOf(note);
  if (index >= 0) {
    heldOrder.splice(index, 1);
  }

  const slot = auditionSlot();
  const actions = getLabActions();
  if (!actions) {
    return;
  }

  const mode = getVoiceMode?.(slot) ?? "mono";
  if (mode === "poly") {
    actions.instrumentNoteOff?.(slot, note);
    return;
  }

  // Mono legato: only silence when nothing remains; otherwise re-attack last held.
  if (heldOrder.length === 0) {
    actions.instrumentNoteOff?.(slot, note);
    return;
  }
  const next = heldOrder[heldOrder.length - 1]!;
  const vel = heldVelocity.get(next) ?? 0.9;
  actions.instrumentNoteOn?.(slot, next, vel);
}

function releaseAllHeld(): void {
  if (held.size === 0) {
    return;
  }
  const actions = getLabActions();
  const slot = auditionSlot();
  const mode = getVoiceMode?.(slot) ?? "mono";
  const notes = [...held];
  held.clear();
  heldOrder.length = 0;
  heldVelocity.clear();
  if (!actions) {
    return;
  }
  if (mode === "poly") {
    for (const note of notes) {
      actions.instrumentNoteOff?.(slot, note);
    }
    return;
  }
  actions.instrumentNoteOff?.(slot, notes[notes.length - 1] ?? 0);
}

function auditionSlot(): InstrumentSlot {
  return (getAuditionSlot?.() ?? 0) as InstrumentSlot;
}
